import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { useQueryClient } from '@tanstack/react-query';
import { config, configIssues } from './config';

export const userManager = configIssues.length ? null : new UserManager({
  authority: config.authority, client_id: config.clientId, redirect_uri: config.redirectUri,
  response_type: 'code', scope: 'openid email profile', disablePKCE: false,
  automaticSilentRenew: false, loadUserInfo: false,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  metadata: { issuer: config.authority, authorization_endpoint: `${config.domain}/oauth2/authorize`, token_endpoint: `${config.domain}/oauth2/token`, jwks_uri: `${config.authority}/.well-known/jwks.json` },
});

// Share startup across StrictMode mounts: authorization codes are single-use.
let startup: Promise<User | null> | undefined;
export function initializeAuth() {
  return startup ??= (async () => {
    if (!userManager) return null;
    const params = new URLSearchParams(location.search);
    if (params.has('code') || params.has('error')) {
      try {
        const user = await userManager.signinRedirectCallback();
        return user;
      } finally { history.replaceState({}, '', `${location.pathname}#monitors`); }
    }
    const user = await userManager.getUser();
    if (user?.expired) { await userManager.removeUser(); return null; }
    return user;
  })();
}

type AuthState = { user: User | null; loading: boolean; error: string; login: () => Promise<void>; logout: () => Promise<void> };
const AuthContext = createContext<AuthState | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const cache = useQueryClient();
  useEffect(() => {
    let active = true;
    const loaded = (next: User) => { cache.clear(); setUser(next); setError(''); };
    const unloaded = () => { cache.clear(); setUser(null); };
    const expired = () => { unloaded(); setError('Your session has expired. Sign in again to continue.'); void userManager?.removeUser(); };
    userManager?.events.addUserLoaded(loaded);
    userManager?.events.addUserUnloaded(unloaded);
    userManager?.events.addAccessTokenExpired(expired);
    initializeAuth().then(next => { if (active) setUser(next); }).catch(() => { if (active) setError('Sign-in could not be completed. Please start a new sign-in.'); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; userManager?.events.removeUserLoaded(loaded); userManager?.events.removeUserUnloaded(unloaded); userManager?.events.removeAccessTokenExpired(expired); };
  }, [cache]);
  async function login() {
    setError(''); setLoading(true);
    try { await userManager?.clearStaleState(); await userManager?.signinRedirect(); }
    catch { setError('Unable to start sign-in. Check the Cognito configuration and try again.'); setLoading(false); }
  }
  async function logout() {
    await userManager?.removeUser(); cache.clear(); setUser(null);
    const url = new URL(`${config.domain}/logout`);
    url.searchParams.set('client_id', config.clientId); url.searchParams.set('logout_uri', config.logoutUri);
    location.assign(url.href);
  }
  return <AuthContext.Provider value={{ user, loading, error, login, logout }}>{children}</AuthContext.Provider>;
}
export function useAuth() { return useContext(AuthContext)!; }
